import { useEffect, useMemo, useRef, useState } from 'react';
import {
  CONTRACT_STATUSES,
  contractSchema,
  formatSort,
  toWireDate,
  type Contract,
  type ContractStatus,
  type LineItem,
} from '@forecastify/shared';
import { useDuration } from '../../duration';
import { DataList, type DataListColumn, type DataListRow, type SortState } from '@forecastify/ui';
import { useT, type MessageKey, type Translate } from '../../i18n';
import { listLabels } from '../delivery/clients-screen';
import { ListHeader, usePageHeader } from '../page-header';
import { useAvatars } from '../use-avatar';
import { useCalendarLabels } from '../date-field';
import { contractLabel, lineItemContractLabel, lineItemContractOptions } from './contract-label';
import { useServerList } from '../use-server-list';
import {
  rememberedSort,
  useListMemory,
  usePruneIds,
  useRememberEffect,
  useRememberedString,
} from '../list-memory';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C24 · Szerződések — the list. `2fazis.markup.html` 1542–1546.             │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * One `DataList` with the mockup's own columns, in its order: **Kód · Szerződés · Projekt · Típus ·
 * Kezdés · Vég · Sortétel · Becslés · Rögzített · Státusz** — measured out of the running prototype
 * rather than chosen.
 *
 * ⚠️ **`Vég` reads *nyitott* rather than an em-dash when a contract has no end.** `state-rules.md`
 * §5 makes `—` the absent value, and an open-ended framework contract has not got a missing end
 * date — it has, deliberately, no end. The two look identical in a column and mean opposite things.
 *
 * ⚠️ **Search, filters, sort and paging are the server's** — 31 Oct 2026. They ran in memory over
 * the whole of `GET /contracts`, with the note that the day the list paginates the search box
 * becomes a query parameter or silently searches one page. It paginated; they are parameters.
 */

/** ⚠️ Branching on the CODE, and every one of them has its own sentence — rule 5. */
const ERROR_KEY: Readonly<Record<string, MessageKey>> = {
  feature_not_enabled: 'contract.error.notEnabled',
  permission_denied: 'error.permission_denied',
  not_found: 'error.not_found',
  conflict: 'error.conflict',
  validation_failed: 'contract.error.invalid',
  line_item_in_use: 'contract.error.lineItemInUse',
  /* OS-1 · the two refusals a line item's people can draw. On these endpoints the P-1 code can
     only come from naming somebody, so it reads as that rather than as task assignment. */
  line_item_people_need_tm: 'lineItem.error.peopleNeedTm',
  assignee_not_a_project_member: 'lineItem.error.personNotMember',
  /* 21 Oct 2026 · a billable type on an internal project, or a client's contract moved onto one. */
  internal_project_billable: 'lineItem.error.internalBillable',
};

/**
 * The same table, for the two dialogs.
 *
 * ⚠️ **Exported rather than duplicated**: a dialog that renders its own sentence for a refusal the
 * list already names is how one code ends up with two meanings.
 */
export function contractErrorKey(code: string): MessageKey {
  return ERROR_KEY[code] ?? 'error.unavailable';
}

const TYPE_KEY = {
  recurring: 'contract.type.recurring',
  milestone: 'contract.type.milestone',
  service: 'contract.type.service',
} as const;

const STATUS_KEY = {
  draft: 'contract.status.draft',
  active: 'contract.status.active',
  closed: 'contract.status.closed',
} as const;

/** The mockup's own badge weights: a live contract is filled, the other two are quiet. */
const STATUS_VARIANT = {
  draft: 'outline',
  active: 'default',
  closed: 'secondary',
} as const;

/**
 * ⚠️⚠️ **ONE SCREEN, TWO MODES — the mockup's own `cMode`, switched from the PAGE HEADER.**
 * `2fazis.markup.html` 717–724 puts a 220px dropdown where the title would be, and that is the
 * whole navigation: line items are not a second address, they are the same list read down one
 * level. A separate route would need its own filters, its own saved views and its own empty state,
 * all of which mean the same thing.
 *
 * ⚠️⚠️ **AND THE CONCLUSION WAS RIGHT WHILE THE PREMISE WAS NOT — item 24, 15 Sep.** Line items are
 * indeed not a second address; they are not a second MODE either. They are rows of one contract's
 * schedule, and a flat list of every line item in the workspace answers no question anybody has
 * while costing a read of the whole table. The mode, its dropdown and the flat list are gone; a
 * line item opens from the contract that owns it.
 */
export function ContractsScreen({
  nonce,
  onRows,
  serverError,
  onOpen,
  onNew,
  onBulkStatus,
}: {
  /** The route's live counter, bumped by its writes too — `ContractList` re-reads its page. */
  readonly nonce: number;
  /** The page on screen, for the route's drawer and bulk change — `ContractList`'s prop. */
  readonly onRows?: (rows: readonly Contract[]) => void;
  /** The selection's status — `ContractList` carries the argument. Absent without `contract.manage`. */
  readonly onBulkStatus?: (ids: readonly string[], status: ContractStatus) => void;
  /**
   * Opens the create dialog. ⚠️ **Absent when the caller cannot `contract.manage`** — a button
   * that refuses on click is worse than no button, and UI gating is UX (rule 6).
   *
   * ⚠️ **Only in `contract` mode.** A line item is created UNDER a contract, so the mockup puts
   * *Új sortétel* on the contract detail and nothing on this list — a create here would have to
   * ask which contract first, which is the detail with extra steps.
   */
  readonly onNew?: () => void;
  /** An error CODE from the last write, or null. Branching on `code`, never on `message` — rule 5. */
  /**
   * C56 · ⚠️ **The code AND its details.** One refusal — `contract_still_bound` — is an
   * instruction rather than a statement, and it needs three figures out of the envelope.
   */
  readonly serverError?: { code: string; values: Record<string, string> } | null;
  readonly onOpen: (contract: Contract) => void;
}) {
  const t = useT();
  /**
   * ┌───────────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ NO MODE SWITCH — item 24, second pass, 15 Sep.                        │
   * └───────────────────────────────────────────────────────────────────────────┘
   *
   * The header was a dropdown that swapped this screen between *Szerződések* and a flat list of
   * every line item in the workspace. 14 Sep took that list off the project's own tab; the global
   * one survived because it was a different file, and the finding is the same on both:
   * **a line item only means anything under the contract that owns it.** `HSO-C01/2` is a row in a
   * contract's schedule — its number, its period and its estimate are all relative to that
   * contract — and a flat list of four hundred of them across sixty contracts is a table nobody
   * can act on.
   *
   * ⚠️ **And it was expensive to be wrong**: the view read every line item in the workspace to
   * render it.
   */
  usePageHeader(() => <ListHeader title={t('title.contracts')} />, []);

  return (
    <div data-screen="contracts" className="flex min-h-0 flex-1 flex-col">
      {serverError === null || serverError === undefined ? null : (
        <p className="text-destructive border-b px-5 py-2 text-[12px]" role="alert">
          {t(contractErrorKey(serverError.code), serverError.values)}
        </p>
      )}
      {/* ⚠️ Edge to edge — the mockup drops its `DataList` straight into the content area. */}
      <div className="min-h-0 flex-1">
        <ContractList
          nonce={nonce}
          {...(onRows === undefined ? {} : { onRows })}
          onOpen={onOpen}
          {...(onNew === undefined ? {} : { onNew })}
          {...(onBulkStatus === undefined ? {} : { onBulkStatus })}
        />
      </div>
    </div>
  );
}

/**
 * The contracts table — the mockup's `cColumns`, with its toolbar.
 *
 * ⚠️ **Its own component because a project has this table too.** `project-contracts-tab.tsx` shows
 * the same list narrowed to one project, and a second transcription of ten columns is how two
 * lists of the same thing start disagreeing about what `Vég` means.
 */
export function ContractList({
  projectId,
  nonce,
  onRows,
  onOpen,
  onNew,
  onAttach,
  onBulkStatus,
}: {
  /** A project's own tab — its contracts and no one else's. Absent on the register. */
  readonly projectId?: string;
  /** Re-reads the page on screen — the caller's live counter and its writes. */
  readonly nonce: number;
  /**
   * 31 Oct 2026 · the page on screen, each time it arrives. ⚠️ The route opens a contract's drawer
   * and sends a bulk change's versions from these — it holds no other list of contracts.
   */
  readonly onRows?: (rows: readonly Contract[]) => void;
  readonly onOpen: (contract: Contract) => void;
  readonly onNew?: () => void;
  /** C56 · attach an existing client-scoped contract. Only a project's own tab offers it. */
  readonly onAttach?: () => void;
  /**
   * 14 Oct 2026 · the selection's status — `POST /contracts/bulk`. ⚠️ **Absent when the caller
   * cannot `contract.manage`**, like `onNew`: the checkboxes stay, the menu does not (rule 6).
   */
  readonly onBulkStatus?: (ids: readonly string[], status: ContractStatus) => void;
}) {
  const { fmt } = useDuration();
  const t = useT();
  /* 3 Nov 2026 · remembered per workspace and member (`list-memory.ts`); a project's own tab is
     its own list. */
  const memory = useListMemory(projectId === undefined ? 'contracts' : 'project-contracts');
  const [query, setQuery] = useRememberedString(memory, 'search');
  const [sort, setSort] = useState<readonly SortState[]>(() => rememberedSort(memory.restored));
  useRememberEffect(memory, { sort });
  const [type, setType] = useRememberedString(memory, 'type');
  const [status, setStatus] = useRememberedString(memory, 'status');
  /**
   * C56 · ⚠️⚠️ **A FILTER VALUE, NOT A SAVED VIEW.** `PHASE-5.md` §2.5 calls it *"a no
   * project filter value"* and the mockup's own predicate is `if (S.cProjFilter === 'none') { if
   * (this.cHasProj(c)) return false; }` — one option on the existing control rather than a
   * fourth tab nobody would find.
   */
  const [scope, setScope] = useRememberedString(memory, 'scope');
  /**
   * ⚠️⚠️ **START AND END, AS RANGES — round 5 item 14.** *"Kevés a szűrő, kell még kezdés,
   * vég, státusz szűrés."* Status was already here; these two were not.
   *
   * ⚠️ **Ranges rather than menus**, for `list-toolbar.tsx`'s reason about every continuous
   * filter: a menu of dates is either buckets somebody else chose or infinite. One `from..to`
   * string each, either end optional.
   */
  const [startRange, setStartRange] = useRememberedString(memory, 'startRange');
  const [endRange, setEndRange] = useRememberedString(memory, 'endRange');

  /**
   * 31 Oct 2026 · the filters as the server reads them (`contractFilterOf`). A range's ends go
   * separately, and only when set — `8..` sends a lower bound alone.
   */
  const [startsFrom = '', startsTo = ''] = startRange.split('..');
  const [endsFrom = '', endsTo = ''] = endRange.split('..');
  const list = useServerList({
    path: '/contracts',
    params: {
      projectId: projectId ?? '',
      search: query.trim(),
      type,
      scope,
      status,
      startsFrom,
      startsTo,
      endsFrom,
      endsTo,
      sort: formatSort(sort) ?? '',
    },
    schema: contractSchema,
    nonce,
  });
  const contracts = list.rows;
  /* ⚠️ The callback through a ref — a parent's inline arrow is a new function every render, and
     the page is what changed, not the listener. */
  const onRowsRef = useRef(onRows);
  onRowsRef.current = onRows;
  useEffect(() => {
    onRowsRef.current?.(contracts);
  }, [contracts]);

  /* One request per distinct picture for the page — see `use-avatar.ts`. */
  const avatars = useAvatars(contracts.map((contract) => contract.ownerAvatarUrl));
  /* ⚠️ `ListToolbar` lives in `@forecastify/ui` and cannot reach `useT`, so a date bound takes
     the app's calendar vocabulary as a prop — `tasks-screen.tsx` does the same. */
  const calendar = useCalendarLabels();

  /** ⚠️ What a range chip says. One `t()` per branch with a literal key — rule 7's shape. */
  const rangeChip = (value: string): string => {
    const [from = '', to = ''] = value.split('..');
    if (from !== '' && to !== '') return t('list.rangeBoth', { from, to });
    if (from !== '') return t('list.rangeFromOnly', { from });
    return t('list.rangeToOnly', { to });
  };

  /* ⚠️ In the server's order — `CONTRACT_SORT` sorts on the contract's own fields, so the hours
     sort as numbers and the dates as dates rather than as the text the cells carry. */
  const rows: DataListRow[] = contracts.map((contract) => ({
    id: contract.id,
    cells: {
      code: { text: contractLabel(contract), mono: true, muted: true },
      name: { text: contract.name, bold: true },
      /* ⚠️⚠️ **C56 · *Nincs projekt* IS A BADGE IN THE MOCKUP, NOT A BLANK CELL** — see
         `phase5/05-project-contracts.png`, where EST-C01 and VIS-C02 draw it. An empty PROJEKT
         cell would read as data somebody failed to enter. */
      project:
        contract.projectId === null
          ? { badge: { label: t('contract.noProject'), variant: 'outline' as const } }
          : { dot: contract.projectColor ?? '', text: contract.projectName ?? '' },
      /* ⚠️ An em-dash for a project whose client is unset — a legal row here, see `0153`. */
      client: { text: contract.clientName ?? '–', muted: contract.clientName === null },
      type: { badge: { label: t(TYPE_KEY[contract.type]), variant: 'secondary' as const } },
      startsOn: { text: contract.startsOn, mono: true, muted: true },
      endsOn:
        contract.endsOn === null
          ? /* ⚠️ Not an em-dash — see the note on this component. */
            { text: t('contract.openEnded'), muted: true }
          : { text: contract.endsOn, mono: true, muted: true },
      lineItems: { text: String(contract.lineItemCount), mono: true, align: 'right' as const },
      estimate: { text: fmt(contract.estimateHours), mono: true, align: 'right' as const },
      tracked: {
        text: fmt(contract.trackedHours),
        mono: true,
        bold: true,
        align: 'right' as const,
      },
      owner: {
        /* ⚠️⚠️ **THE NAME BESIDE THE FACE — round 5 item 15.** *"A sorra nem elég a felelős
           jele, a nevét is ki kell írni."* Initials in a circle identify somebody you already
           know; on a list of contracts across a project the reader is finding out WHO. */
        text: contract.ownerName,
        avatar: {
          initials: initialsOf(contract.ownerName),
          ...(contract.ownerAvatarSeed === null ? {} : { seed: contract.ownerAvatarSeed }),
          title: contract.ownerName,
          src: avatars.get(contract.ownerAvatarUrl ?? ''),
        },
      },
      status: {
        badge: { label: t(STATUS_KEY[contract.status]), variant: STATUS_VARIANT[contract.status] },
      },
    },
  }));

  return (
    <DataList
      columnsKey="contracts"
      card={{
        title: 'name',
        context: 'code',
        defaults: {
          badge: 'status',
          avatar: 'owner',
          details: ['project', 'type', 'endsOn', 'tracked'],
        },
      }}
      isLoading={list.loading}
      columns={columns(t)}
      rows={rows}
      page={list.page}
      sort={sort}
      onSortChange={setSort}
      search={query}
      onSearchChange={setQuery}
      filters={[
        {
          key: 'type',
          kind: 'multi' as const,
          label: t('contract.column.type'),
          value: type,
          onChange: setType,
          options: [
            { value: 'recurring', label: t('contract.type.recurring') },
            { value: 'milestone', label: t('contract.type.milestone') },
            { value: 'service', label: t('contract.type.service') },
          ],
        },
        {
          /* ⚠️ Beside the type, which is the other thing that says what KIND of contract. */
          key: 'scope',
          label: t('contract.column.project'),
          value: scope,
          onChange: setScope,
          options: [
            { value: 'project', label: t('contract.scope.onProject') },
            { value: 'none', label: t('contract.noProject') },
          ],
        },
        {
          key: 'status',
          kind: 'multi' as const,
          label: t('contract.column.status'),
          value: status,
          onChange: setStatus,
          options: [
            { value: 'draft', label: t('contract.status.draft') },
            { value: 'active', label: t('contract.status.active') },
            { value: 'closed', label: t('contract.status.closed') },
          ],
        },
        {
          key: 'startsOn',
          label: t('contract.column.startsOn'),
          value: startRange,
          onChange: setStartRange,
          kind: 'range' as const,
          input: 'date' as const,
          calendar,
          today: toWireDate(new Date()),
          options: [],
          display: (value: string) => rangeChip(value),
        },
        {
          key: 'endsOn',
          label: t('contract.column.endsOn'),
          value: endRange,
          onChange: setEndRange,
          kind: 'range' as const,
          input: 'date' as const,
          calendar,
          today: toWireDate(new Date()),
          options: [],
          display: (value: string) => rangeChip(value),
        },
      ]}
      /**
       * C56 · ⚠️⚠️ **A SPLIT BUTTON WHEN THERE IS SOMETHING TO ATTACH** — the mockup's
       * `+ Új szerződés ⌄` (`phase5/12-project-contracts-tab.png`). `ToolbarAction.items` already
       * does exactly this — *"given items, the action becomes a dropdown instead of a button"* —
       * so the shape was there and no component changed.
       *
       * ⚠️ **One plain button when `onAttach` is absent**, which is every screen outside a
       * project's own tab: attaching is something you do TO a project.
       */
      actions={
        onNew === undefined
          ? []
          : onAttach === undefined
            ? [{ label: t('contract.action.new'), icon: 'plus', primary: true, onSelect: onNew }]
            : [
                {
                  label: t('contract.action.new'),
                  icon: 'plus',
                  primary: true,
                  items: [
                    { label: t('contract.action.createNew'), icon: 'plus', onSelect: onNew },
                    {
                      label: t('contract.action.attachExisting'),
                      icon: 'link',
                      onSelect: onAttach,
                    },
                  ],
                },
              ]
      }
      /**
       * ⚠️ **The statuses in the contract's own order — draft, active, closed — and the filter's
       * own labels**, so the menu and the status column cannot name the same state two ways. One
       * `t()` per entry with a literal key, through `STATUS_KEY`, for `i18n:check`.
       */
      bulkActions={
        onBulkStatus === undefined
          ? []
          : [
              {
                label: t('contract.column.status'),
                icon: 'circle-dot' as const,
                menu: (ids: string[]) =>
                  CONTRACT_STATUSES.map((next) => ({
                    label: t(STATUS_KEY[next]),
                    onSelect: () => {
                      onBulkStatus(ids, next);
                    },
                  })),
              },
            ]
      }
      onRowClick={(id) => {
        const contract = contracts.find((candidate) => candidate.id === id);
        if (contract !== undefined) onOpen(contract);
      }}
      empty={{
        icon: 'file-text',
        title: t('contracts.empty.title'),
        body: t('contracts.empty.body'),
        /* ⚠️ **The empty state gets the button too** — `2fazis.markup.html` 875. A first contract
           is created from the screen that has none, and sending somebody back to a toolbar they
           have already read past is how an empty product stays empty. */
        ...(onNew === undefined ? {} : { actionLabel: t('contract.action.new'), onAction: onNew }),
      }}
      labels={{ ...listLabels(t), search: t('contracts.search') }}
    />
  );
}

/**
 * The line items across every contract — the mockup's `liColumns`, in its order and at its widths
 * (`2fazis.main.jsx` 4315–4325): **Sortétel · Szerződés · Projekt · Hozzárendelés · Feladat ·
 * Időszak · Becslés · Rögzített · Státusz**.
 *
 * ⚠️ **`Hozzárendelés` reads *kézi* for every row today**, and the column is here anyway: the value
 * is on the wire and C26 gives it a second one. A column that appears with the feature would hide
 * the fact that manual is a choice rather than the absence of one.
 */
export function LineItemList({
  items,
  onOpen,
}: {
  readonly items: readonly LineItem[];
  readonly onOpen: (item: LineItem) => void;
}) {
  const { fmt } = useDuration();
  const t = useT();
  /* 3 Nov 2026 · remembered (`list-memory.ts`); a contract since gone is pruned once items load. */
  const memory = useListMemory('contract-line-items');
  const [query, setQuery] = useRememberedString(memory, 'search');
  const [contract, setContract] = useRememberedString(memory, 'contractId');
  const knownContracts = useMemo(
    () => (items.length === 0 ? null : new Set(items.map((item) => item.contractId))),
    [items],
  );
  usePruneIds(contract, knownContracts, setContract);

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return items.filter(
      (item) =>
        (needle === '' ||
          `${item.name} ${item.identifier} ${lineItemContractLabel(item)} ${item.contractName}`
            .toLowerCase()
            .includes(needle)) &&
        (contract === '' || item.contractId === contract),
    );
  }, [items, query, contract]);

  const rows: DataListRow[] = shown.map((item) => ({
    id: item.id,
    cells: {
      name: { text: item.name, bold: true },
      contract: { text: lineItemContractLabel(item), mono: true, muted: true },
      assignment: {
        text: t(item.assignment === 'rule' ? 'lineItem.byRule' : 'lineItem.byHand'),
        muted: true,
      },
      tasks: { text: String(item.taskCount), mono: true, align: 'right' as const },
      period: {
        /* ⚠️ An em-dash for a line item with no window of its own — it inherits the contract's,
           which is a different fact from "nobody set one" and is not this column's to state. */
        text:
          item.startsOn === null && item.endsOn === null
            ? '–'
            : `${item.startsOn ?? '…'} – ${item.endsOn ?? '…'}`,
        mono: true,
        muted: true,
      },
      estimate: { text: fmt(item.estimateHours), mono: true, align: 'right' as const },
      tracked: {
        text: fmt(item.trackedHours),
        mono: true,
        bold: true,
        align: 'right' as const,
      },
      status: {
        badge: {
          label: t(item.status === 'closed' ? 'lineItem.closed' : 'lineItem.open'),
          variant: item.status === 'closed' ? ('secondary' as const) : ('default' as const),
        },
      },
    },
  }));

  return (
    <DataList
      columnsKey="line-items"
      card={{
        title: 'name',
        context: 'contract',
        defaults: { badge: 'status', details: ['assignment', 'period', 'estimate', 'tracked'] },
      }}
      columns={[
        { key: 'name', label: t('contract.lineItem'), sortKey: 'name' },
        { key: 'contract', label: t('contract.column.name'), width: 120 },
        { key: 'assignment', label: t('lineItem.assignment'), width: 130 },
        { key: 'tasks', label: t('contract.tasks'), width: 92, align: 'right' },
        { key: 'period', label: t('lineItem.period'), width: 180 },
        { key: 'estimate', label: t('contract.column.estimate'), width: 100, align: 'right' },
        { key: 'tracked', label: t('contract.column.tracked'), width: 108, align: 'right' },
        { key: 'status', label: t('contract.column.status'), width: 116 },
      ]}
      rows={rows}
      search={query}
      onSearchChange={setQuery}
      filters={[
        {
          key: 'contract',
          label: t('contract.column.name'),
          value: contract,
          onChange: setContract,
          /* 3 Nov 2026 · the contract's name, its identifier beside it (`contractOptions`). */
          options: lineItemContractOptions(items),
        },
      ]}
      onRowClick={(id) => {
        const item = items.find((candidate) => candidate.id === id);
        if (item !== undefined) onOpen(item);
      }}
      empty={{
        icon: 'list-checks',
        title: t('lineItems.empty.title'),
        body: t('lineItems.empty.body'),
      }}
      footerNote={t('lineItems.footer', { count: shown.length })}
      labels={{ ...listLabels(t), search: t('lineItems.search') }}
    />
  );
}

function columns(t: Translate): DataListColumn[] {
  return [
    { key: 'code', label: t('list.identifier'), width: 120, sortKey: 'code' },
    { key: 'name', label: t('contract.column.name'), sortKey: 'name' },
    { key: 'project', label: t('contract.column.project'), width: 190, sortKey: 'project' },
    /**
     * C56 · ⚠️⚠️ **BESIDE THE PROJECT, NOT INSTEAD OF IT** — `phase5/05-project-contracts.png`
     * draws both. A project-scoped contract shows a project AND the client it is for; a
     * client-scoped one shows *Nincs projekt* and the client is the only subject it has.
     */
    { key: 'client', label: t('contract.column.client'), width: 190, sortKey: 'client' },
    { key: 'type', label: t('contract.column.type'), width: 130, sortKey: 'type' },
    { key: 'startsOn', label: t('contract.column.startsOn'), width: 110, sortKey: 'startsOn' },
    { key: 'endsOn', label: t('contract.column.endsOn'), width: 110, sortKey: 'endsOn' },
    {
      key: 'lineItems',
      label: t('contract.column.lineItems'),
      width: 90,
      align: 'right',
      sortKey: 'lineItems',
    },
    {
      key: 'estimate',
      label: t('contract.column.estimate'),
      width: 100,
      align: 'right',
      sortKey: 'estimate',
    },
    {
      key: 'tracked',
      label: t('contract.column.tracked'),
      width: 100,
      align: 'right',
      sortKey: 'tracked',
    },
    /* ⚠️ 70px held a face and nothing else — round 5 item 15 put a name beside it. */
    { key: 'owner', label: t('contract.column.owner'), width: 170, sortKey: 'owner' },
    { key: 'status', label: t('contract.column.status'), width: 110, sortKey: 'status' },
  ];
}

/** Two initials, first and last word — the rule every other avatar in this product uses. */
function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const first = words[0]?.[0] ?? '';
  const last = words.length > 1 ? (words[words.length - 1]?.[0] ?? '') : '';
  return `${first}${last}`.toUpperCase();
}

/**
 * ⚠️⚠️ **A DATE INSIDE A `from..to` BOUND — round 5 item 14.** Empty bounds pass everything,
 * which is what *no filter* means; a `null` date passes only when nothing was asked, because a
 * contract with no end is not an answer to *which end before March*.
 *
 * ⚠️ Wire dates compare as strings: `YYYY-MM-DD` sorts chronologically by construction, which
 * is the one thing that format is for.
 */
export function within(date: string | null, range: string): boolean {
  if (range === '') return true;
  if (date === null) return false;
  const [from = '', to = ''] = range.split('..');
  return (from === '' || date >= from) && (to === '' || date <= to);
}
